import { reactive } from 'cdui-js';

/**
 * 底栏（上一步/下一步）共享槽。
 *
 * 背景：`LastNextBtns` 原本由各步骤组件在滚动容器内部渲染，`position: fixed` 在安卓
 * WebView 上会退化成相对滚动内容定位，导致键盘弹起时底栏随页面上下滚动而跳动。
 *
 * 方案 A：把固定栏 DOM 提到滚动容器之外（`LastNextBarPortal`，挂在 `OpenProcess` 顶层），
 * 步骤组件仍照旧渲染 `<LastNextBtns .../>`，但 `LastNextBtns` 只把回调/状态注册到本槽，
 * 并在滚动内容里保留占位 spacer；真正的底栏由 `LastNextBarPortal` 读取本槽渲染。
 *
 * - `last` / `next`：步骤传入的回调（函数引用，步骤内一次性创建，基本稳定）。
 * - `loading` / `nextDisabled`：以 getter 形式存入，portal 在响应式作用内调用以保持追踪。
 * - `hideLast` / `nextLabel`：可选；提交成功页仅「完成」、隐藏「上一步」。
 * - `active`：是否有步骤注册了底栏；无底栏的页面（CheckMethod / EditErrorInfo）保持 false。
 * - `suppressed`：EditErrorInfo 等自管底栏的遮罩层打开时为 true，Portal 不渲染，
 *   避免与纠错底栏抢点击（同级 z-index 时 Portal DOM 更靠后会盖住纠错按钮）。
 */
export type BarSlot = {
  last: (() => void) | null;
  next: (() => void | Promise<void>) | null;
  loading: () => boolean;
  nextDisabled: () => boolean;
  hideLast: () => boolean;
  nextLabel: () => string | null;
  active: boolean;
  suppressed: boolean;
  /**
   * 纠错自管底栏切页时置位。Portal 被 suppress 时不显示，
   * 交还 Portal（如末步进入风险披露）后继续挡住连点，直到目标页画出。
   */
  transitioning: boolean;
};

export const barSlot = reactive<BarSlot>({
  last: null,
  next: null,
  loading: () => false,
  nextDisabled: () => false,
  hideLast: () => false,
  nextLabel: () => null,
  active: false,
  suppressed: false,
  transitioning: false,
});

/** EditErrorInfo 等自管底栏时调用；与 setBarSlot 解耦，不依赖子表单是否注册过槽 */
export function setBarPortalSuppressed(suppressed: boolean) {
  barSlot.suppressed = suppressed;
}

/** 自管底栏切页期间让 Portal 的 isBusy 保持为真，避免交还底栏的空窗被连点 */
export function setBarTransitioning(transitioning: boolean) {
  barSlot.transitioning = transitioning;
}

/**
 * 等浏览器把已经提交的步骤 DOM 画出来再解锁。
 * 步骤号在回调里是同步改的，若不挡到下一帧，慢机器上后续点击会在绘制前入队，画面仍停在旧页。
 * rAF 在后台页可能不跑，800ms 后兜底放开，避免底栏一直转圈。
 */
export function waitForOpenProgressPaint(): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const timer = setTimeout(finish, 800);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        clearTimeout(timer);
        finish();
      });
    });
  });
}

/** 单调递增的注册令牌：仅当前最新注册者拥有清理权，避免步骤切换时旧实例清掉新实例的槽 */
let ownerTag = 0;

export type BarSlotProps = {
  last?: () => void;
  next?: () => void | Promise<void>;
  loading?: boolean;
  nextDisabled?: boolean;
  /** 为 true 时不渲染「上一步 / 返回」 */
  hideLast?: boolean;
  /** 覆盖右侧主按钮文案（如「完成」） */
  nextLabel?: string;
};

/**
 * 注册底栏回调/状态到共享槽。返回 cleanup：仅当本实例仍是最新注册者时才回收，
 * 避免步骤 A→B 切换时 A 的 cleanup 把 B 已写入的槽清空。
 */
export function setBarSlot(props: BarSlotProps): () => void {
  const tag = ++ownerTag;
  barSlot.last = props.last ?? null;
  barSlot.next = props.next ?? null;
  // getter 闭包捕获 props 对象（cdui-js 响应式代理），portal 调用时实时读取
  barSlot.loading = () => props.loading ?? false;
  barSlot.nextDisabled = () => props.nextDisabled ?? false;
  barSlot.hideLast = () => !!props.hideLast;
  barSlot.nextLabel = () => props.nextLabel ?? null;
  barSlot.active = true;
  return () => {
    if (tag !== ownerTag) return;
    barSlot.active = false;
    barSlot.last = null;
    barSlot.next = null;
    barSlot.loading = () => false;
    barSlot.nextDisabled = () => false;
    barSlot.hideLast = () => false;
    barSlot.nextLabel = () => null;
  };
}
